import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCwIcon } from 'lucide-react';
import { Order } from './types';
import { useRestaurant } from '../context/RestaurantContext';
import { supabase } from '../lib/supabase';
const OPEN_STATUSES = ['new', 'accepted', 'preparing', 'ready', 'out_for_delivery'];
interface SupabaseTable {
  id: string;
  table_number: number;
  name: string | null;
  capacity: number | null;
  is_active: boolean;
  created_at: string;
}
interface TableInfo {
  id: string;
  number: number;
  name: string | null;
  capacity: number | null;
  occupied: boolean;
  serviceMode: 'qr' | 'staff' | null;
  orderCount: number;
  statusLabel: string;
}
interface TableMapPanelProps {
  orders: Order[];
}
export function TableMapPanel({
  orders
}: TableMapPanelProps) {
  const {
    restaurantId
  } = useRestaurant();
  const [supabaseTables, setSupabaseTables] = useState<SupabaseTable[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchTables = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const {
        data,
        error
      } = await supabase.from('tables').select('*').eq('restaurant_id', restaurantId).eq('is_active', true).order('table_number', {
        ascending: true
      });
      if (error) {
        console.error('Error fetching tables:', error);
        return;
      }
      setSupabaseTables(data || []);
    } catch (err) {
      console.error('Error fetching tables:', err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);
  useEffect(() => {
    fetchTables();
    const interval = setInterval(fetchTables, 30000);
    return () => clearInterval(interval);
  }, [fetchTables]);
  const tables: TableInfo[] = useMemo(() => {
    const tableOrders = new Map<string, Order[]>();
    orders.forEach(o => {
      if (o.table_number && OPEN_STATUSES.includes(o.status)) {
        const key = String(o.table_number).trim();
        const existing = tableOrders.get(key) || [];
        existing.push(o);
        tableOrders.set(key, existing);
      }
    });
    return supabaseTables.map(t => {
      const key = String(t.table_number).trim();
      const active = tableOrders.get(key) || [];
      const occupied = active.length > 0;
      let serviceMode: 'qr' | 'staff' | null = null;
      if (occupied) {
        const latest = [...active].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        serviceMode = latest.service_mode as 'qr' | 'staff' || null;
      }
      let statusLabel = '';
      if (occupied) {
        if (active.some(o => o.status === 'ready')) statusLabel = 'Listo';else if (active.some(o => o.status === 'preparing')) statusLabel = 'Preparando';else if (active.some(o => o.status === 'accepted')) statusLabel = 'Aceptado';else statusLabel = 'Nuevo';
      }
      return {
        id: t.id,
        number: t.table_number,
        name: t.name,
        capacity: t.capacity,
        occupied,
        serviceMode,
        orderCount: active.length,
        statusLabel
      };
    });
  }, [orders, supabaseTables]);
  const totalTables = tables.length;
  const occupiedCount = tables.filter(t => t.occupied).length;
  const qrCount = tables.filter(t => t.serviceMode === 'qr').length;
  const staffCount = tables.filter(t => t.serviceMode === 'staff').length;
  if (loading) {
    return <div className="bg-surface rounded-2xl border border-gray-800 p-6" data-id="element-543">
        <div className="flex items-center gap-3 text-gray-500" data-id="element-544">
          <RefreshCwIcon className="w-4 h-4 animate-spin" data-id="element-545" />
          <span className="text-sm" data-id="element-546">Cargando mesas...</span>
        </div>
      </div>;
  }
  if (totalTables === 0) {
    return <div className="bg-surface rounded-2xl border border-gray-800 p-6" data-id="element-547">
        <h3 className="font-heading text-lg text-white flex items-center gap-2 mb-3" data-id="element-548">
          🗺️ Mapa de Mesas
        </h3>
        <p className="text-gray-500 text-sm text-center py-6" data-id="element-549">
          No hay mesas configuradas. Agregá mesas en la tabla{' '}
          <code className="text-gray-400 bg-carbon px-1.5 py-0.5 rounded text-xs" data-id="element-550">
            tables
          </code>{' '}
          de Supabase.
        </p>
      </div>;
  }
  return <div className="bg-surface rounded-2xl border border-gray-800 p-6" data-id="element-551">
      <div className="flex items-center justify-between mb-5" data-id="element-552">
        <h3 className="font-heading text-lg text-white flex items-center gap-2" data-id="element-553">
          🗺️ Mapa de Mesas
          <span className="px-2 py-0.5 bg-dorado/15 text-dorado text-xs font-bold rounded-full border border-dorado/25" data-id="element-554">
            {occupiedCount}/{totalTables}
          </span>
        </h3>
        <div className="flex items-center gap-3 text-xs" data-id="element-555">
          <button onClick={fetchTables} className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-surface-light" title="Recargar mesas" data-id="element-556">
            <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-557" />
          </button>
          <span className="flex items-center gap-1.5" data-id="element-558">
            <span className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-559" />
            <span className="text-gray-500" data-id="element-560">Libre</span>
          </span>
          <span className="flex items-center gap-1.5" data-id="element-561">
            <span className="w-2 h-2 rounded-full bg-amber-400" data-id="element-562" />
            <span className="text-gray-500" data-id="element-563">Ocupada</span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-id="element-564">
        {tables.map(table => <div key={table.id} className={`relative rounded-xl border p-4 text-center transition-all ${table.occupied ? 'bg-amber-500/5 border-amber-500/30' : 'bg-surface-light border-gray-700'}`} data-id="element-565">
            {table.serviceMode && <div className="absolute top-2 right-2" data-id="element-566">
                <span className="text-sm" title={table.serviceMode === 'qr' ? 'Autoatención QR' : 'Atendida por personal'} data-id="element-567">
                  {table.serviceMode === 'qr' ? '📱' : '👤'}
                </span>
              </div>}
            <div className={`absolute top-2 left-2 w-2 h-2 rounded-full ${table.occupied ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400/50'}`} data-id="element-568" />
            <div className={`text-2xl font-bold mb-0.5 ${table.occupied ? 'text-amber-400' : 'text-gray-500'}`} data-id="element-569">
              {table.number}
            </div>
            {table.name && <div className="text-[10px] text-gray-400 mb-1 truncate px-1" data-id="element-570">
                {table.name}
              </div>}
            <div className="text-[10px] font-medium uppercase tracking-wider mb-1" data-id="element-571">
              {table.occupied ? <span className="text-amber-400" data-id="element-572">{table.statusLabel}</span> : <span className="text-emerald-400/70" data-id="element-573">Libre</span>}
            </div>
            {table.capacity && <div className="text-[10px] text-gray-600" data-id="element-574">
                {table.capacity} {table.capacity === 1 ? 'persona' : 'personas'}
              </div>}
            {table.occupied && <div className="space-y-0.5 mt-1" data-id="element-575">
                <div className="text-[10px] text-gray-500" data-id="element-576">
                  {table.orderCount}{' '}
                  {table.orderCount === 1 ? 'pedido' : 'pedidos'}
                </div>
              </div>}
          </div>)}
      </div>
    </div>;
}